/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { createRef, useCallback, useEffect, useMemo, useState } from 'react'

import styled, { css } from 'styled-components'

import CopyButton from 'src/components/CopyButton'
import { GEODE_URL } from 'src/constants'
import { useModalContext } from 'src/contexts/ModalContext'
import { shortenAddress } from 'src/functions/address/shortenAddress'
import { calculateDuration } from 'src/functions/time'
import { useMediaScreenType } from 'src/hooks/useMediaScreenType'

// import { usePollContext } from '../../../contexts/PollProvider'
import { useThemeContext } from '../../../contexts/ThemeContext'
import { pxToEm, pxToVw } from '../../../functions/units'
import Avatar from '../../Avatar'
import { PrimaryButton } from '../../Button'
import FlexBox from '../../Common/FlexBox'
import Icon from '../../Icon'

export type Props = {
  expiredAt: string
  qrCodeImage?: string
  id: string
  txId: string
}

const DonateQR = ({ expiredAt, qrCodeImage, id, txId }: Props) => {
  const { colors } = useThemeContext()
  const { closeModal, modalIsOpen, openedModalStack } = useModalContext()

  const { isMobile } = useMediaScreenType()

  const qrCodeImageRef = createRef<HTMLImageElement>()

  const handleDownloadQr = useCallback(async () => {
    if (qrCodeImageRef.current) {
      const resp = await fetch(`${GEODE_URL}/qr-image/${id}`)
      const blob = await resp.blob()

      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `tgb-qrpayment-${new Date().getTime()}.png`
      a.click()
      URL.revokeObjectURL(a.href)
    }
  }, [qrCodeImageRef, id])

  const [timeLeft, setTimeLeft] = useState<
    | 0
    | {
        minutes: number
        seconds: number
      }
  >()

  const humanizedTimeLeft = useMemo(() => {
    if (timeLeft === undefined) return 'Loading...'
    if (timeLeft === 0) return 'Expired'

    const zeroPaddedSeconds = timeLeft.seconds < 10 ? `0${timeLeft.seconds}` : timeLeft.seconds
    return `${timeLeft.minutes}:${zeroPaddedSeconds}`
  }, [timeLeft])

  useEffect(() => {
    if (expiredAt && timeLeft !== 0) {
      const timer = setTimeout(() => {
        setTimeLeft(calculateDuration(new Date(expiredAt)))
        clearTimeout(timer)
      }, 1000)

      return () => {
        clearTimeout(timer)
      }
    } else if (timeLeft === 0 && modalIsOpen && openedModalStack.includes(txId)) {
      closeModal(txId)
    }
  }, [closeModal, expiredAt, modalIsOpen, openedModalStack, timeLeft, txId])

  return (
    <FlexBox rowGap={16} flexDirection="column">
      <StyledArticle>
        <FlexBox as="header" columnGap={12} alignItems="center">
          <Icon icon="caret-right-circle" color={colors.primary600} size={26} />
          <h2>How to make this payment?</h2>
        </FlexBox>

        <p>
          <em>
            <strong>Desktop:</strong>
          </em>{' '}
          Scan the QR code with your ibanking app and complete the donation!
        </p>
        <p>
          <em>
            <strong>Mobile:</strong>
          </em>{' '}
          Screenshot the QR Code and upload it to any app that supports PayNow to finish your
          donation!
        </p>
      </StyledArticle>

      <StyledAvatar circular={false} size={pxToVw(360, isMobile ? 720 : 1920)}>
        {qrCodeImage && (
          <StyledQR ref={qrCodeImageRef} src={`data:image/png;base64,${qrCodeImage}`} alt="" />
        )}
      </StyledAvatar>

      <StyledHorizontalLine />

      <>
        {process.env.REACT_APP_ENV === 'staging' && txId ? (
          <>
            <StyledWireDetailRow justifyContent="space-between" alignItems="center">
              <span>Transaction ID:</span>
              <FlexBox alignItems="center" columnGap={9}>
                <em>{shortenAddress(txId, 14, 5)}</em>
                <CopyButton text={txId} />
              </FlexBox>
            </StyledWireDetailRow>

            <StyledHorizontalLine />
          </>
        ) : null}

        <StyledWireDetailRow justifyContent="space-between" alignItems="center">
          <span>Payment expired at:</span>
          <FlexBox alignItems="center" columnGap={9}>
            <em>{humanizedTimeLeft}</em>
            <StyledRowIcon as="span">
              <Icon icon="timer" size={19} color={colors.gray600} />
            </StyledRowIcon>
          </FlexBox>
        </StyledWireDetailRow>
      </>

      <StyledHorizontalLine />

      <StyledDownloadButton outlined onClick={handleDownloadQr}>
        Download Image
      </StyledDownloadButton>
    </FlexBox>
  )
}

export default DonateQR

const StyledArticle = styled.article`
  h2 {
    margin: 0;
    font-size: ${pxToVw(24)};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.gray500};
  }

  p {
    margin: 0;
    margin-top: ${pxToVw(15)};
    font-size: ${pxToVw(18)};
    line-height: ${pxToEm(24, 18)};
    color: ${({ theme }) => theme.colors.gray};
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      h2 {
        font-size: ${pxToVw(24, 720)};
      }
      p {
        margin-top: ${pxToVw(15, 720)};
        font-size: ${pxToVw(18, 720)};
      }
    `}
  `}
`

const StyledAvatar = styled(Avatar)`
  display: inline-flex;
  margin-inline: auto;
  max-width: 390px;
  max-height: 390px;

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile``}
  `}
`

const StyledQR = styled.img`
  object-fit: contain !important;
`

const StyledDownloadButton = styled(PrimaryButton)`
  height: ${pxToVw(44)};
  font-size: ${pxToVw(18)};
  width: 100%;

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      display: none;
      height: ${pxToVw(44, 720)};
      font-size: ${pxToVw(18, 720)};
    `}
  `}
`

const StyledHorizontalLine = styled.hr`
  width: 100%;
  border: none;
  border-top: solid 1px ${({ theme }) => theme.colors.gray200};
  margin-block: ${pxToVw(8)};

  ${({ theme }) => theme.mediaQuery.mobile`
    margin-block: ${pxToVw(8, 720)};
  `}
`

const StyledWireDetailRow = styled(FlexBox)`
  margin-block: ${pxToVw(-12)};
  width: 100%;

  > span {
    font-weight: 500;
    font-size: ${pxToVw(20)};
    color: ${({ theme }) => theme.colors.gray500};
  }

  em {
    font-size: ${pxToVw(20)};
    color: ${({ theme }) => theme.colors.gray};
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      margin-block: ${pxToVw(-12, 720)};

      > span {
        font-size: ${pxToVw(20, 720)};
      }

      em {
        font-size: ${pxToVw(20, 720)};
      }
    `}
  `}
`

const StyledRowIcon = styled.span`
  padding: 4px;
  height: fit-content;
  display: inline-flex;

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      padding: ${pxToVw(4, 720)};
    `}
  `}
`
